import { useState } from 'react';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { bookingDetailsRoute } from '@/features/dashboard/bookingChanges/routes/bookingDetailsRoute.ts';
import { DetailsMode } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { toChangePackageStatus } from '@/features/dashboard/bookingChanges/types/BookingChangeDetailDto.mapper.ts';
import { DetailsProvider } from '@/features/dashboard/bookingChanges/contexts/DetailsContext.ts';
import useBookingBillView from '@/features/dashboard/bookingChanges/hooks/useBookingBillView.ts';
import useChangeDraft from '@/features/dashboard/bookingChanges/hooks/useChangeDraft.ts';
import useGetBookingDetails from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import { DetailsLayout, useIsTabMenu } from '@/components/details';
import WithdrawalModal from '@/features/dashboard/bookingChanges/components/withdrawal/WithdrawalModal.tsx';
import {
  useEditField,
  useExitEditing,
} from '@/features/dashboard/bookingChanges/components/editing';
import { DetailsSidebar } from '@/features/dashboard/bookingChanges/components/sidebar';
import {
  BookingDetailsHeader,
  BookingDetailsModals,
} from '@/features/dashboard/bookingChanges/components/bookingDetails';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import TotalBar from '@/components/ui/TotalBar.tsx';
import { BookingStatus } from '@/types/BookingStatus.ts';

/**
 * Szkielet ekranu rezerwacji (C1/C2) — poziom całej paczki. Treść zakładki
 * wstawia router przez `<Outlet />`: „Rezerwacja” to akordeon osób z pozycjami
 * i płatnościami, „Historia” — paczki, które już weszły w życie.
 */
const BookingDetailsPage = () => {
  const { bookingId } = bookingDetailsRoute.useParams();
  const search = bookingDetailsRoute.useSearch();

  /*
   * Na menu ekran ma być samym nagłówkiem i wyborem zakładki — panel boczny
   * i belka wartości zmian należą do treści, a treści tu jeszcze nie ma.
   */
  const isTabMenu = useIsTabMenu({
    to: '/app/bookings/$bookingId',
    params: { bookingId },
  });
  const navigate = useNavigate();
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);

  const { draft, change } = useChangeDraft(bookingId);
  // Nie z `view`, bo `view` zależy od `isEditing`.
  const bookingCancelled =
    useGetBookingDetails(bookingId).data?.status === BookingStatus.CANCELLED;

  /*
   * Tryb edycji wymaga szkicu, więc nie wystarczy `?mode=edit` w adresie:
   * pod ten adres da się wejść linkiem, odświeżeniem strony albo cofnięciem
   * po odrzuceniu paczki. Ekran wyglądał wtedy na gotowy do edycji, a każdy
   * zapis pozycji cicho przepadał, bo nie miał do czego trafić.
   *
   * Warunek na szkicu zamiast przekierowania jest odporny na wyścig: między
   * założeniem paczki a jej pojawieniem się w zapytaniu ekran po prostu
   * jeszcze nie pokazuje edycji, zamiast odbijać się do podglądu.
   *
   * Anulowana rezerwacja nie ma przełącznika trybu, więc `?mode=edit` albo
   * szkic sprzed anulowania nie mogą jej zostawić w edycji bez wyjścia.
   */
  const isEditing =
    search.mode === DetailsMode.EDIT && draft !== null && !bookingCancelled;

  const { view } = useBookingBillView(bookingId, isEditing);

  /** Zmiana trybu zostaje na bieżącej zakładce — `.` to aktualna ścieżka. */
  const goToMode = (mode: DetailsMode) =>
    navigate({ to: '.', search: { mode } });

  const exitToView = () => goToMode(DetailsMode.VIEW);

  const exitEditing = useExitEditing({
    bookingId,
    draft,
    draftStatus: change ? toChangePackageStatus(change.status) : null,
    onEnterEditing: () => goToMode(DetailsMode.EDIT),
    onExitToView: exitToView,
  });

  const editField = useEditField(bookingId);

  const openBooking = () =>
    navigate({
      to: '/app/bookings/$bookingId/booking',
      params: { bookingId },
    });

  return (
    <DetailsProvider
      value={{
        bookingId,
        bookingCode: view?.bookingCode ?? '',
        isEditing,
        onlyChanged,
        onOnlyChangedChange: setOnlyChanged,
        editField,
        onOpenBooking: openBooking,
      }}
    >
      <DetailsLayout
        header={
          <BookingDetailsHeader
            bookingId={bookingId}
            search={search}
            view={view}
            isEditing={isEditing}
            draft={draft}
            editAvailability={exitEditing.availability}
            onToggleEditing={exitEditing.toggleEditing}
            onWithdrawPackage={exitEditing.withdrawPackage}
            isWithdrawing={exitEditing.isDiscarding}
            onOpenWithdrawal={
              view && view.status !== BookingStatus.CANCELLED
                ? () => setIsWithdrawalOpen(true)
                : undefined
            }
            onlyChanged={onlyChanged}
            onOnlyChangedChange={setOnlyChanged}
          />
        }
        sidebar={
          isTabMenu ? null : (
            <DetailsSidebar
              bookingId={bookingId}
              isEditing={isEditing}
              onExitEditing={exitToView}
            />
          )
        }
        bottomBar={
          isEditing && draft && !isTabMenu ? (
            <TotalBar
              label="Wartość zmian"
              value={
                <MoneyAmountList values={draft.totalDelta} variant="signed" />
              }
            />
          ) : null
        }
      >
        <Outlet />

        {isWithdrawalOpen && view && (
          <WithdrawalModal
            bookingId={bookingId}
            participants={view.participants}
            onClose={() => setIsWithdrawalOpen(false)}
          />
        )}

        <BookingDetailsModals
          draft={draft}
          isExitModalOpen={exitEditing.isExitModalOpen}
          isDiscarding={exitEditing.isDiscarding}
          onExitChoice={exitEditing.resolveExit}
          onExitDismiss={exitEditing.dismissExit}
          editField={editField}
        />
      </DetailsLayout>
    </DetailsProvider>
  );
};

export default BookingDetailsPage;
